import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, Grid, CircularProgress } from "@mui/material";
import InputIcon from "@mui/icons-material/Input";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SpeedIcon from "@mui/icons-material/Speed";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PeopleIcon from "@mui/icons-material/People";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import KpiCard from "./KpiCard";
import ExportButtons from "./ExportButtons";
import ReportFilters from "./ReportFilters";
import { exportToPDF, exportToExcel } from "../../../utils/exportUtils";
import RelatorioDistribuidorService from "../../../service/relatorio_distribuidor_service";
import { toLocalISODate } from "../../../utils/dateUtils";

const COLORS = ["#1a9e8f", "#3498db", "#f5a623", "#dc2626", "#9c27b0", "#00897b", "#c0ca33"];

function getDefaultDates() {
	const hoje = new Date();
	const inicio = new Date(hoje);
	inicio.setDate(inicio.getDate() - 30);
	return {
		from: toLocalISODate(inicio),
		to: toLocalISODate(hoje),
	};
}

function ReportVisaoGeral() {
	const defaults = getDefaultDates();
	const [dateFrom, setDateFrom] = useState(defaults.from);
	const [dateTo, setDateTo] = useState(defaults.to);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);

	useEffect(() => {
		loadData();
	}, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadData = async () => {
		try {
			setLoading(true);
			setError(null);
			const service = new RelatorioDistribuidorService();
			const result = await service.fetchVisaoGeral(dateFrom, dateTo);
			setData(result);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const raw = data?.kpis ?? {};
	const kpis = {
		totalEntradas: raw.totalEntradas ?? 0,
		quantidadeEntrada: raw.quantidadeEntrada ?? raw.pesoEntrada ?? 0,
		totalVendas: raw.totalVendas ?? raw.totalSaidas ?? 0,
		quantidadeVendida: raw.quantidadeVendida ?? raw.pesoSaida ?? 0,
		eficiencia: raw.eficiencia ?? 0,
		totalPerda: 0,
		fornecedoresAtivos: raw.fornecedoresAtivos ?? 0,
		clientesAtivos: raw.clientesAtivos ?? raw.destinosAtivos ?? 0,
	};

	// Eficiência = vendido / entrada
	const eficiencia = kpis.eficiencia > 0
		? kpis.eficiencia
		: (kpis.quantidadeEntrada > 0
			? (kpis.quantidadeVendida / kpis.quantidadeEntrada) * 100
			: 0);

	const pieData = data?.entradaPorProduto ?? [];
	const barData = (data?.fluxoDiario ?? []).map((d) => ({
		data: new Date(d.data + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
		Entradas: d.entrada,
		Vendas: d.venda ?? d.saida ?? 0,
	}));

	const getExportConfig = () => ({
		title: "Relatório Visão Geral - Distribuição",
		filename: "visao-geral",
		kpis: [
			{ label: "Total Entradas", value: `${kpis.totalEntradas} registros (${kpis.quantidadeEntrada.toLocaleString("pt-BR")} un)` },
			{ label: "Total Vendido", value: `${kpis.totalVendas} vendas (${kpis.quantidadeVendida.toLocaleString("pt-BR")} un)` },
			{ label: "Eficiência", value: `${eficiencia.toFixed(1)}%` },
			{ label: "Perdas", value: `${kpis.totalPerda.toLocaleString("pt-BR")} kg` },
		],
		headers: ["Produto", "Qtd Entrada", "% do Total"],
		rows: pieData.map((p) => [p.name, p.value, `${((p.value / (kpis.quantidadeEntrada || 1)) * 100).toFixed(1)}%`]),
	});

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
				<ReportFilters
					searchValue=""
					onSearchChange={() => {}}
					searchPlaceholder=""
					dateFrom={dateFrom}
					dateTo={dateTo}
					onDateFromChange={setDateFrom}
					onDateToChange={setDateTo}
				/>
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, gap: 1, color: "#8e9bae" }}>
					<CircularProgress size={20} /> <Typography variant="body2">Carregando dados...</Typography>
				</Box>
			) : error ? (
				<Box sx={{ py: 6, textAlign: "center", color: "#dc2626" }}>
					<Typography variant="body2">Erro ao carregar dados: {error}</Typography>
				</Box>
			) : (<>
			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<InputIcon />} title="Entradas" value={String(kpis.totalEntradas)} subtitle={`${kpis.quantidadeEntrada.toLocaleString("pt-BR")} un`} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<ShoppingCartIcon />} title="Vendas" value={String(kpis.totalVendas)} subtitle={`${kpis.quantidadeVendida.toLocaleString("pt-BR")} un`} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<SpeedIcon />} title="Eficiência" value={`${eficiencia.toFixed(1)}%`} trend={eficiencia > 80 ? "up" : "down"} trendValue="aproveitamento" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<TrendingDownIcon />} title="Perdas" value={`${kpis.totalPerda.toLocaleString("pt-BR")} kg`} trend="down" trendValue={kpis.quantidadeEntrada > 0 ? `${((kpis.totalPerda / kpis.quantidadeEntrada) * 100).toFixed(1)}%` : "0%"} />
				</Grid>
			</Grid>

			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<ExportButtons onExportPDF={() => exportToPDF(getExportConfig())} onExportExcel={() => exportToExcel(getExportConfig())} />
			</Box>

			<Grid container spacing={2.5}>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
						<CardContent>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 2, fontSize: "0.9rem" }}>
								Fluxo Diário (Entradas vs Vendas)
							</Typography>
							<Box sx={{ height: 260 }}>
								{barData.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={barData}>
											<CartesianGrid strokeDasharray="3 3" stroke="#dfe3e8" />
											<XAxis dataKey="data" tick={{ fontSize: 11 }} />
											<YAxis tick={{ fontSize: 11 }} />
											<Tooltip contentStyle={{ borderRadius: "8px", fontSize: 12 }} />
											<Legend wrapperStyle={{ fontSize: 12 }} />
											<Bar dataKey="Entradas" fill="#1a9e8f" radius={[3, 3, 0, 0]} />
											<Bar dataKey="Vendas" fill="#3498db" radius={[3, 3, 0, 0]} />
										</BarChart>
									</ResponsiveContainer>
								) : (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#8e9bae" }}>
										<Typography variant="body2">Nenhum dado no período</Typography>
									</Box>
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
						<CardContent>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 2, fontSize: "0.9rem" }}>
								Distribuição por Produto (Entrada)
							</Typography>
							<Box sx={{ height: 260 }}>
								{pieData.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<PieChart>
											<Pie
												data={pieData}
												cx="50%"
												cy="50%"
												innerRadius={55}
												outerRadius={90}
												paddingAngle={3}
												dataKey="value"
												label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
												labelLine={false}
												style={{ fontSize: 10 }}
											>
												{pieData.map((_, i) => (
													<Cell key={i} fill={COLORS[i % COLORS.length]} />
												))}
											</Pie>
											<Tooltip formatter={(value) => `${value.toLocaleString("pt-BR")}`} contentStyle={{ borderRadius: "8px", fontSize: 12 }} />
										</PieChart>
									</ResponsiveContainer>
								) : (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#8e9bae" }}>
										<Typography variant="body2">Nenhum dado no período</Typography>
									</Box>
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			<Grid container spacing={2.5}>
				<Grid size={{ xs: 12, md: 6 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
						<CardContent>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
								<PeopleIcon sx={{ fontSize: 18, color: "#1a9e8f" }} />
								<Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0f4557" }}>Fornecedores Ativos</Typography>
							</Box>
							<Typography variant="h4" sx={{ fontWeight: 700, color: "#0f4557" }}>{kpis.fornecedoresAtivos}</Typography>
							<Typography variant="caption" sx={{ color: "#8e9bae" }}>fornecedores com entradas no período</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
						<CardContent>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
								<StorefrontIcon sx={{ fontSize: 18, color: "#1a9e8f" }} />
								<Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0f4557" }}>Clientes Ativos</Typography>
							</Box>
							<Typography variant="h4" sx={{ fontWeight: 700, color: "#0f4557" }}>{kpis.clientesAtivos}</Typography>
							<Typography variant="caption" sx={{ color: "#8e9bae" }}>clientes com vendas no período</Typography>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			</>)}
		</Box>
	);
}

export default ReportVisaoGeral;
